import {isAxiosError} from "axios";
import {queryHttp} from "../../../query/queryHttp";
import {CHAT_CONFIG} from "../../chat-agent/services/ChatConfiguration";
import {type DashboardItem, type DashboardList, normalizeDashgenList, normalizeHermesDetail, normalizeHermesList, UNAVAILABLE_LIST} from "./dashboardsMapping";

/**
 * Dashboards module API (cat-ai-proc-agent, base = the agent `API_URL`).
 *
 * - Built-in: Hermes dashboards an admin published for the whole deployment
 *   (`GET /superset/hermes-dashboards`, `GET /superset/hermes-dashboards/{id}`).
 * - Yours: the user's DashGen chat dashboards for a databag
 *   (`GET /superset/dashboards?databag_id=`).
 *
 * Every `/superset` route 404s when Superset is disabled on the agent; the
 * fetchers turn that into `available: false` so the module can hide itself.
 */

export * from "./dashboardsMapping";

// region urls
// include_domain=true asks for {superset_domain, dashboards}; older agents ignore it and send the bare list.
export const hermesDashboardsUrl = () => `${CHAT_CONFIG.API_URL}/superset/hermes-dashboards`;
export const hermesDashboardUrl = (id: string) =>
    `${CHAT_CONFIG.API_URL}/superset/hermes-dashboards/${encodeURIComponent(id)}`;
export const myDashboardsUrl = (databagId: number) =>
    `${CHAT_CONFIG.API_URL}/superset/dashboards?databag_id=${databagId}&include_domain=true`;
// endregion

// region fetchers
function isNotFound(err: unknown): boolean {
    return isAxiosError(err) && err.response?.status === 404;
}

export async function fetchHermesDashboards(signal?: AbortSignal): Promise<DashboardList> {
    if (!CHAT_CONFIG.API_URL) return UNAVAILABLE_LIST;
    try {
        const {data} = await queryHttp.get(hermesDashboardsUrl(), {signal});
        return normalizeHermesList(data);
    } catch (err) {
        if (isNotFound(err)) return UNAVAILABLE_LIST;
        throw err;
    }
}

export async function fetchMyDashboards(databagId: number, signal?: AbortSignal): Promise<DashboardList> {
    if (!CHAT_CONFIG.API_URL || !databagId) return UNAVAILABLE_LIST;
    try {
        const {data} = await queryHttp.get(myDashboardsUrl(databagId), {signal});
        return normalizeDashgenList(data);
    } catch (err) {
        if (isNotFound(err)) return UNAVAILABLE_LIST;
        throw err;
    }
}

/** Null when the listing is gone (404). */
export async function fetchHermesDashboard(id: string, signal?: AbortSignal): Promise<DashboardItem | null> {
    if (!CHAT_CONFIG.API_URL) return null;
    try {
        const {data} = await queryHttp.get(hermesDashboardUrl(id), {signal});
        return normalizeHermesDetail(data);
    } catch (err) {
        if (isNotFound(err)) return null;
        throw err;
    }
}
// endregion
